/**
 * LegacyRedirect Component
 * Handles 301-style permanent redirects from legacy URLs to new CMS-driven URLs
 * 
 * Purpose: Prevents SEO loss from broken links, ensures old bookmarks and 
 * Google-indexed URLs still work.
 */

import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * Comprehensive map of legacy URL patterns to new CMS slugs
 * Format: 'legacy-path-segment' -> 'cms-slug'
 */
const LEGACY_SLUG_MAP = {
  // ============================================================
  // CANADA PAGES
  // ============================================================
  'nova-scotia-nominee-program-nsnp-your-connection-to-canadian-immigration': 'nova-scotia-nominee-program-nsnp',
  'nova-scotia-nominee-program-nsnp': 'nova-scotia-nominee-program-nsnp',
  'manitoba-pnp': 'manitoba-pnp',
  'atlantic-immigration-program': 'atlantic-immigration-program',
  'canada-immigration': 'canada-immigration',
  'how-to-immigrate-to-canada': 'how-to-immigrate-to-canada',
  'ontario-pnp': 'ontario-pnp',
  'alberta-pnp': 'alberta-pnp',
  'british-columbia-pnp-program': 'british-columbia-pnp-program',
  'canada-spouse-visa': 'canada-spouse-visa',
  'canada-points-calculator-for-permanent-residency': 'canada-points-calculator-for-permanent-residency',
  'nursing-jobs-in-canada': 'nursing-jobs-in-canada',
  'requirements-canada-immigration-visa': 'requirements-canada-immigration-visa',
  'occupation-list-of-canada': 'occupation-list-of-canada',
  'comprehensive-ranking-system': 'comprehensive-ranking-system',
  'latest-draw-express-entry-program': 'latest-draw-express-entry-program',
  'minimum-ielts-score-for-canada': 'minimum-ielts-score-for-canada',
  'average-salary-in-canada': 'average-salary-in-canada',
  'canada-family-visa': 'canada-family-visa',
  'canada-parents-visa': 'canada-parents-visa',
  'canada-pnp-skilled-workers': 'canada-pnp-skilled-workers',
  'jobs-in-canada-for-indians': 'jobs-in-canada-for-indians',
  'quebec-skilled-worker-program': 'quebec-skilled-worker-program',
  'canada-pr-points-system': 'comprehensive-ranking-system',
  'canada-work-visa': 'canada-work-visa',
  'canada-visit-visa': 'canada-visit-visa',
  'canada-study-visa': 'canada-study-visa',
  'canada-entrepreneur-visa': 'canada-entrepreneur-visa',
  'canada-business-visa-faqs': 'canada-business-visa-faqs',
  
  // ============================================================
  // AUSTRALIA PAGES
  // ============================================================
  'nsw-state-nomination': 'nsw-state-nomination',
  'new-south-wales-occupation-list-for-subclass-190': 'new-south-wales-occupation-list-for-subclass-190',
  'victoria-state-nomination': 'victoria-state-nomination',
  'australia-immigration': 'australia-immigration',
  'occupation-list': 'occupation-list',
  'australia-pr-points-calculator': 'australia-pr-points-calculator',
  'australian-immigration-requirements': 'australian-immigration-requirements',
  'australia-national-innovation-visa': 'australia-national-innovation-visa',
  'settle-down-in-australia-with-the-national-innovation-visa': 'settle-down-in-australia-with-the-national-innovation-visa',
  'australias-new-core-skills-occupation-list': 'australias-new-core-skills-occupation-list',
  'australia-point-system': 'australia-point-system',
  'south-australia-state-nomination-skilled-occupation-list': 'south-australia-state-nomination-skilled-occupation-list',
  'tasmanian-skilled-occupation-list': 'tasmanian-skilled-occupation-list',
  'western-australian-skilled-migration-occupation-list': 'western-australian-skilled-migration-occupation-list',
  'queensland-skilled-occupation-list': 'queensland-skilled-occupation-list',
  'business-investment-temporary-subclass-188-visa': 'business-investment-temporary-subclass-188-visa',
  'australia-business-subclass-888-visa': 'australia-business-subclass-888-visa',
  'australia-visit-visa': 'australia-visit-visa',
  'australia-work-visa': 'australia-work-visa',
  'australia-study-visa': 'australia-study-visa',
  
  // ============================================================
  // UK PAGES
  // ============================================================
  'uk-family-visa': 'uk-family-visa',
  'uk-innovator-founder-visa': 'uk-innovator-founder-visa',
  'how-to-immigrate-to-uk': 'how-to-immigrate-to-uk',
  'uk-visa': 'uk-visa',
  'uk-scale-up-worker-visa': 'uk-scale-up-worker-visa',
  'uk-global-talent-visa': 'uk-talent-program',
  'uk-talent-program': 'uk-talent-program',
  'jobs-in-the-uk-for-indians': 'jobs-in-the-uk-for-indians',
  'uk-innovator-visa': 'uk-innovator-visa',
  'uk-investor-visa': 'uk-investor-visa',
  'isle-of-man-innovator-visa-programme': 'isle-of-man-innovator-visa-programme',
  
  // ============================================================
  // USA PAGES
  // ============================================================
  'h1b-visa-guide': 'h1b-visa-guide',
  'eb2-niw-visa': 'eb2-niw-visa',
  'usa-immigration': 'usa-immigration',
  'us-visa': 'us-visa',
  'average-salary-in-usa': 'average-salary-in-usa',
  'b-2-visitor-visa': 'b-2-visitor-visa',
  'b-1-visa': 'b-1-visa',
  'family-based-green-card': 'family-based-green-card',
  'usa-l1-visa': 'usa-l1-visa',
  'usa-visit-visa': 'usa-visit-visa',
  'us-study-visa': 'us-study-visa',
  
  // ============================================================
  // GERMANY & EUROPE PAGES
  // ============================================================
  'germany-skilled-immigration-act': 'germany-skilled-immigration-act',
  'germany-immigration': 'germany-immigration',
  'europe-visa': 'europe-visa',
  'germany-eu-blue-card': 'germany-eu-blue-card',
  'germany-job-seeker-visa': 'germany-job-seeker-visa',
  'portugal-job-seeker-visa': 'portugal-job-seeker-visa',
  'netherlands-job-seeker-visa-your-complete-guide': 'netherlands-job-seeker-visa-your-complete-guide',
  'austria-job-seeker-visa': 'austria-job-seeker-visa',
  'sweden-job-seeker-visa': 'sweden-job-seeker-visa',
  'netherlands-start-up-visa': 'netherlands-start-up-visa',
  'hungary-golden-visa': 'hungary-golden-visa',
  'greece-golden-visa': 'greece-golden-visa',
  'latvia-trp-program': 'latvia-trp-program',
  'schengen-visit-visa': 'schengen-visit-visa',
  'europe-study-visa': 'europe-study-visa',
  
  // ============================================================
  // UAE & OTHER REGIONS
  // ============================================================
  'uae-golden-visa-guide': 'uae-golden-visa-guide',
  'uae-work-visa': 'uae-work-visa',
  'nz-skilled-program': 'nz-skilled-program',
};

/**
 * Category fallback pages for unmapped URLs
 */
const CATEGORY_FALLBACKS = {
  'canada': '/countries/canada',
  'australia': '/skilled/australia/australia-pr-visa',
  'usa': '/business/usa/eb-5-visa',
  'uk': '/skilled/uk/uk-skilled-worker-visa',
  'germany': '/germany-opportunity-card',
  'europe': '/skilled/europe/europe-visa',
  'uae': '/skilled/uae/uae-golden-visa',
  'new-zealand': '/skilled/new-zealand/new-zealand-pr-visa',
};

/**
 * Log unmapped redirect attempts for debugging
 */
const logUnmappedRedirect = (originalPath, resolvedPath) => {
  console.warn(`[LegacyRedirect] Unmapped URL redirect:
  Original: ${originalPath}
  Resolved to: ${resolvedPath}
  Consider adding explicit mapping for better SEO.`);
};

/**
 * Extract the slug from a path and find the best CMS redirect
 * @param {string} pathname - The full URL pathname
 * @returns {object} - { slug, isExactMatch, category }
 */
const resolveRedirect = (pathname) => {
  const pathParts = pathname.split('/').filter(Boolean);
  const lastSegment = pathParts[pathParts.length - 1];
  
  // Determine category from path
  let category = null;
  if (pathname.includes('/canada')) category = 'canada';
  else if (pathname.includes('/australia')) category = 'australia';
  else if (pathname.includes('/usa')) category = 'usa';
  else if (pathname.includes('/uk')) category = 'uk';
  else if (pathname.includes('/germany') || pathname.includes('/europe')) category = 'europe';
  else if (pathname.includes('/uae')) category = 'uae';
  else if (pathname.includes('/new-zealand')) category = 'new-zealand';
  
  // Check for exact slug match
  if (LEGACY_SLUG_MAP[lastSegment]) {
    return {
      slug: LEGACY_SLUG_MAP[lastSegment],
      isExactMatch: true,
      category
    };
  }
  
  // Fallback to using the last segment as slug
  return {
    slug: lastSegment,
    isExactMatch: false,
    category
  };
};

/**
 * Main LegacyRedirect component
 * Performs 301-style permanent redirects to CMS pages
 */
export const LegacyRedirect = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    const { slug, isExactMatch, category } = resolveRedirect(location.pathname);
    
    if (!slug) {
      // No slug found - redirect to category page or home
      const fallback = category ? CATEGORY_FALLBACKS[category] : '/';
      logUnmappedRedirect(location.pathname, fallback);
      navigate(fallback, { replace: true });
      return;
    }
    
    const targetPath = `/cms/${slug}`;
    
    if (!isExactMatch) {
      logUnmappedRedirect(location.pathname, targetPath);
    }
    
    // Perform permanent redirect (replace: true simulates 301 behavior in SPA)
    navigate(targetPath, { replace: true });
  }, [location.pathname, navigate]);
  
  // Show nothing while redirecting (instant redirect)
  return null;
};

/**
 * Catch-all redirect handler for any unmatched skilled/business/visit/study paths
 * Attempts to resolve to CMS page, falls back to category page
 */
export const CatchAllRedirect = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    const { slug, isExactMatch, category } = resolveRedirect(location.pathname);
    
    // Try CMS page first, then category fallback, then home
    if (slug) {
      const targetPath = `/cms/${slug}`;
      if (!isExactMatch) {
        logUnmappedRedirect(location.pathname, targetPath);
      }
      navigate(targetPath, { replace: true });
    } else if (category && CATEGORY_FALLBACKS[category]) {
      logUnmappedRedirect(location.pathname, CATEGORY_FALLBACKS[category]);
      navigate(CATEGORY_FALLBACKS[category], { replace: true });
    } else {
      logUnmappedRedirect(location.pathname, '/');
      navigate('/', { replace: true });
    }
  }, [location.pathname, navigate]);
  
  return null;
};

/**
 * Utility: Check if a slug has a known redirect mapping
 */
export const hasRedirectMapping = (slug) => {
  return LEGACY_SLUG_MAP.hasOwnProperty(slug);
};

/**
 * Utility: Get all mapped legacy slugs (for sitemap generation)
 */
export const getAllLegacySlugs = () => {
  return Object.keys(LEGACY_SLUG_MAP);
};

export default LegacyRedirect;
